/**
 * GraphQL Type Definitions
 *
 * Type-safe definitions matching the AWS AppSync GraphQL schema.
 * These types correspond to the DynamoDB models and GraphQL API responses.
 *
 * @see /amplify/data/resource.ts - GraphQL schema definition
 * @see /amplify/data/SCHEMA_CHANGES_PHASE1.md - Schema documentation
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Tariff Category Enum
 * Distinguishes between household (G) and business (C) tariffs
 */
export enum TariffCategory {
  HOUSEHOLD = 'HOUSEHOLD',   // G-tariffs for residential customers
  BUSINESS = 'BUSINESS',     // C-tariffs for business customers
}

/**
 * Business Power Group Enum
 * Polish business tariff power groups
 */
export enum BusinessPowerGroup {
  C1 = 'C1',   // Small business (≤40 kW contracted power)
  C2 = 'C2',   // Medium/Large business (>40 kW contracted power)
}

/**
 * Tariff Type Enum
 * Polish energy tariff types - Household (G) and Business (C)
 */
export enum TariffType {
  // === HOUSEHOLD TARIFFS (G-series) ===
  G11 = 'G11',     // Single-rate tariff (flat price 24/7)
  G12 = 'G12',     // Two-rate tariff (day/night split)
  G12as = 'G12as', // Anti-smog tariff (electric heating optimization)
  G12w = 'G12w',   // Weekend tariff (weekend all-day discount)
  G12n = 'G12n',   // PGE-specific night tariff (tania noc 01:00-05:00 + weekendy)
  G12r = 'G12r',   // Energa-specific extended weekend (dłuższa noc 21:00-07:00)
  G13 = 'G13',     // Three-rate seasonal tariff (morning/evening peaks)
  G13s = 'G13s',   // Dynamic renewable-aligned tariff (solar production hours)
  G14 = 'G14',     // Dynamic hourly pricing based on market RCE prices

  // === BUSINESS TARIFFS (C-series) ===
  // C1 Group (≤40 kW) - Small businesses
  C11 = 'C11',     // Single-rate business tariff (flat price 24/7)
  C12a = 'C12a',   // Two-rate business tariff (day/night fixed zones)
  C12b = 'C12b',   // Two-rate business tariff (seasonal variation)

  // C2 Group (>40 kW) - Medium/Large businesses
  C21 = 'C21',     // Single-rate large business tariff
  C22a = 'C22a',   // Two-rate large business tariff (day/night)
  C22b = 'C22b',   // Two-rate large business tariff (seasonal)
}

/**
 * Consumption Pattern Enum
 * Predefined consumption patterns
 */
export enum ConsumptionPatternType {
  UNIFORM = 'UNIFORM',                 // Uniform consumption throughout the day
  WORK_FROM_HOME = 'WORK_FROM_HOME',   // Higher daytime usage
  MORNING_EVENING = 'MORNING_EVENING', // Peak usage morning and evening
  SUGGESTED = 'SUGGESTED',             // AI-suggested pattern based on household
  CUSTOM = 'CUSTOM'                    // User-defined custom pattern
}

// ============================================================================
// PRICING STRUCTURES (JSON Fields)
// ============================================================================

/**
 * Time Zone Schedule
 * Defines when a specific time zone (day/night/peak) is active
 */
export interface TimeZoneSchedule {
  daysOfWeek: string[];      // Empty array = all days
  startTime: string;         // HH:MM format
  endTime: string;           // HH:MM format
  hoursPerDay: number;       // Number of hours in this zone
}

/**
 * Time Zone Definition
 * Defines a time zone (e.g., "DAY", "NIGHT")
 */
export interface TimeZone {
  zoneCode: string;          // "DAY", "NIGHT", "PEAK", "ALL_DAY"
  zoneName: string;          // Display name
  description: string;       // Human-readable description
  schedule: TimeZoneSchedule[];
}

/**
 * Time Zone Variant
 * Different time zone configurations (e.g., summer vs winter for G13)
 */
export interface TimeZoneVariant {
  variantId: string;         // "YEAR_ROUND", "SUMMER", "WINTER"
  variantName: string;       // Display name
  appliesFrom?: string;      // Start date (YYYY-MM-DD)
  appliesTo?: string;        // End date (YYYY-MM-DD)
  timeZones: TimeZone[];
}

/**
 * Energy Price Components
 * Breakdown of energy price
 */
export interface EnergyPriceComponents {
  energyBase: number;        // Base energy price (net)
  vat: number;               // VAT amount
}

/**
 * Energy Price by Zone
 * Energy pricing for a specific time zone
 */
export interface EnergyPriceByZone {
  zoneCode: string;          // "DAY", "NIGHT", "PEAK", "ALL_DAY"
  grossPrice: number;        // Price with VAT (PLN/kWh)
  netPrice: number;          // Price without VAT (PLN/kWh)
  components: EnergyPriceComponents;
}

/**
 * Energy Prices Structure
 * Complete energy pricing data
 */
export interface EnergyPrices {
  pricesByZone: EnergyPriceByZone[];
  isGovernmentFrozen: boolean;
  frozenUntil?: string;      // Date string (YYYY-MM-DD)
}

/**
 * Distribution Price Components
 * Breakdown of distribution price
 */
export interface DistributionPriceComponents {
  distributionVariable: number;  // Variable distribution cost (net)
  qualityCharge: number;         // Quality service charge (net)
  vat: number;                   // VAT amount
}

/**
 * Distribution Price by Zone
 * Distribution pricing for a specific time zone
 */
export interface DistributionPriceByZone {
  zoneCode: string;          // "DAY", "NIGHT", "PEAK", "ALL_DAY"
  grossPrice: number;        // Price with VAT (PLN/kWh)
  netPrice: number;          // Price without VAT (PLN/kWh)
  components: DistributionPriceComponents;
}

/**
 * Distribution Prices Structure
 * Complete distribution pricing data
 */
export interface DistributionPrices {
  variablePricesByZone: DistributionPriceByZone[];
  notes?: string;
}

/**
 * Fixed Fee by Connection Type
 * Monthly fixed fees
 */
export interface FixedFeeByConnection {
  connectionType: string;    // "SINGLE_PHASE" or "THREE_PHASE"
  grossMonthlyFee: number;   // Fee with VAT (PLN/month)
  netMonthlyFee: number;     // Fee without VAT (PLN/month)
}

/**
 * Fixed Fees Structure
 * Complete fixed fee data
 */
export interface FixedFees {
  feesByConnection: FixedFeeByConnection[];
}

/**
 * Additional Charge
 * Extra charges (e.g., quality service charge)
 */
export interface AdditionalCharge {
  chargeType: string;        // "QUALITY", "TRANSITION", etc.
  chargeName: string;        // Display name
  grossAmount: number;       // Amount with VAT
  netAmount: number;         // Amount without VAT
  unit: string;              // "PLN_PER_KWH", "PLN_PER_MONTH"
}

/**
 * Price Freeze Information
 * Government price freeze details
 */
export interface PriceFreeze {
  active: boolean;
  maxGrossPrice: number;     // Maximum frozen price (PLN/kWh)
  freezeStart?: string;      // Start date (YYYY-MM-DD)
  freezeEnd?: string;        // End date (YYYY-MM-DD)
  description?: string;
}

/**
 * Estimated Annual Cost
 * Reference cost estimate
 */
export interface EstimatedAnnualCost {
  annualConsumption: number; // kWh per year
  estimatedCostGross: number; // Total cost with VAT (PLN)
  consumptionPattern: string; // Pattern type used
  assumptions: string;       // Calculation assumptions
}

// ============================================================================
// GRAPHQL MODELS
// ============================================================================

/**
 * DistributorPricing Model
 * Reference pricing data for electricity distributors and tariffs
 *
 * @see /amplify/data/resource.ts - DistributorPricing model
 */
export interface DistributorPricing {
  id: string;

  // Identifiers
  distributorCode: string;   // "TAURON", "PGE", "ENEA", "ENERGA"
  distributorName: string;   // "Tauron Dystrybucja"
  tariffCode: string;        // "G11", "G12", "G12w", "G13"
  tariffName: string;        // Display name
  regionCode: string;        // "DEFAULT" or region-specific

  // Pricing version
  versionId: string;         // "TAURON_2025_V1"
  ureApprovalNumber?: string; // URE decision reference
  validFrom: string;         // Date string (YYYY-MM-DD)
  validTo: string;           // Date string (YYYY-MM-DD)

  // Price freeze info (JSON)
  priceFreeze?: PriceFreeze;

  // Time zone definitions (JSON)
  timeZoneVariants: TimeZoneVariant[];

  // Pricing data (JSON)
  energyPrices: EnergyPrices;
  distributionPrices: DistributionPrices;
  fixedFees: FixedFees;
  additionalCharges?: AdditionalCharge[];
  estimatedAnnualCost?: EstimatedAnnualCost;

  // Tariff information
  description?: string;
  servesRegions?: string[];  // Polish voivodeships

  // Metadata
  isActive: boolean;
  notes?: string;

  // Timestamps (auto-generated by Amplify)
  createdAt: string;
  updatedAt: string;
}

/**
 * Monthly Result
 * Monthly cost breakdown for a specific tariff
 */
export interface MonthlyResult {
  month: number;             // Month number (1-12)
  tariff: string;            // Tariff code (G11, G12)
  consumption: number;       // Total kWh consumed
  productionCost: number;    // Energy production cost (PLN, net)
  distributionCost: number;  // Distribution cost (PLN, net)
  totalCost: number;         // Total cost with VAT (PLN, gross)
  dayKwh?: number;           // Day consumption (G12 only)
  nightKwh?: number;         // Night consumption (G12 only)
}

/**
 * Annual Result by Tariff
 * Annual summary for a specific tariff
 */
export interface AnnualResultByTariff {
  tariff: string;            // Tariff code (G11, G12)
  totalCost: number;         // Total annual cost (PLN, gross)
  totalKwh: number;          // Total annual consumption (kWh)
  productionCost: number;    // Total production cost (PLN, net)
  distributionCost: number;  // Total distribution cost (PLN, net)
  avgCostPerKwh: number;     // Average cost per kWh (PLN, gross)
  savingsVsG11?: number;     // Savings compared to G11 (PLN)
}

/**
 * Annual Results Structure
 * Complete annual comparison results
 */
export interface AnnualResults {
  G11: AnnualResultByTariff;
  G12: AnnualResultByTariff;
}

/**
 * Hourly Pattern
 * Hourly consumption multipliers for weekday and weekend
 */
export interface HourlyPattern {
  weekday: number[];         // 24 hourly multipliers (0-23h)
  weekend: number[];         // 24 hourly multipliers (0-23h)
}

/**
 * TariffCalculation Model
 * User tariff calculation results
 *
 * @see /amplify/data/resource.ts - TariffCalculation model
 */
export interface TariffCalculation {
  id: string;

  // Owner reference
  userId: string;            // Cognito User ID

  // Input parameters
  distributor: string;       // "TAURON" (Phase 1 only)
  voivodeship?: string;      // User's region
  currentTariff: TariffType; // User's current tariff

  // Consumption data
  annualConsumption: number; // Total kWh per year
  monthlyConsumption?: number[]; // Array[12] of monthly kWh values
  consumptionPattern: ConsumptionPatternType;
  hourlyPattern?: HourlyPattern;

  // Household characteristics
  householdSize?: number;    // Number of people (1-10)
  hasHeatPump?: boolean;
  hasSolarPanels?: boolean;
  hasElectricVehicle?: boolean;

  // Calculation results (JSON)
  monthlyResults: MonthlyResult[];
  annualResults: AnnualResults;
  recommendedTariff?: string; // Best tariff recommendation
  potentialSavings?: number;  // Annual savings vs current tariff (PLN)

  // Metadata
  calculatedAt: string;      // ISO datetime string

  // Timestamps (auto-generated by Amplify)
  createdAt: string;
  updatedAt: string;

  // Owner field (auto-generated by Amplify)
  owner?: string;
}

// ============================================================================
// GRAPHQL QUERY RESPONSES
// ============================================================================

/**
 * List Response Wrapper
 * Standard Amplify list query response structure
 */
export interface ListResponse<T> {
  items: T[];
  nextToken?: string;
}

/**
 * Pricing By Distributor Query Response
 */
export interface PricingByDistributorResponse {
  pricingByDistributor: ListResponse<DistributorPricing>;
}

/**
 * Calculations By User Query Response
 */
export interface CalculationsByUserResponse {
  calculationsByUser: ListResponse<TariffCalculation>;
}

/**
 * Create Tariff Calculation Mutation Response
 */
export interface CreateTariffCalculationResponse {
  createTariffCalculation: TariffCalculation;
}

/**
 * Get Distributor Pricing Query Response
 */
export interface GetDistributorPricingResponse {
  getDistributorPricing: DistributorPricing;
}

/**
 * Get Tariff Calculation Query Response
 */
export interface GetTariffCalculationResponse {
  getTariffCalculation: TariffCalculation;
}
